import axios from 'axios';
import { backendBaseUrl5 } from './config';
import {
    convertGameData,
    convertConfig,
    beforeSaveGame,
    BackendError,
} from './shared';

const masterApi = axios.create({
    baseURL: `${backendBaseUrl5}master/`,
});

export function callAction5(token, action, parameters = {}, post = false) {
    return new Promise((resolve, reject) => {
        const params = {
            key: token,
            ...parameters,
        };
        let call;
        if (post) {
            let formdata = new FormData();
            for (const [key, value] of Object.entries(params)) {
                formdata.append(key, value);
            }
            call = masterApi.post(action, formdata);
        } else {
            call = masterApi.get(action, {
                params,
            });
        }
        call.then((axResult) => {
            if (axResult?.data?.responsecode === 0) {
                resolve(axResult.data.data);
            } else if (axResult?.data?.responsemsg) {
                reject(new BackendError(axResult.data.responsemsg));
            } else {
                reject(new Error(`${action} error`));
            }
        }).catch((axiosError) => {
            // console.log('axiosError:', axiosError);
            reject(JSON.stringify(axiosError, 4));
        });
    });
}

/**
 * Master settings and game list
 * @param {*} token
 * @returns
 */
export function login(token) {
    return new Promise((resolve, reject) => {
        callAction5(token, '/PageLoad')
            .then((data) => {
                resolve({
                    name: data?.settings?.name,
                    email: data?.settings?.email,
                    permissionLevel: 2,
                    configs: data?.settings?.configs?.map((_configKey) => {
                        return {
                            ...convertConfig(data.configs[_configKey]),
                            _configKey,
                        };
                    }),
                });
            })
            .catch(reject);
    });
}

export function getGames(token) {
    return callAction5(token, 'GetGames');
}

export function newGame(token, config) {
    return new Promise((resolve, reject) => {
        callAction5(token, 'NewGame', { configname: config._configKey })
            .then((game) => {
                resolve(convertGameData(game, config));
            })
            .catch(reject);
    });
}

export function getGame(token, gamename) {
    return new Promise((resolve, reject) => {
        callAction5(token, 'GetGame', { nameOrToken: gamename })
            .then((game) => {
                resolve(game);
            })
            .catch(reject);
    });
}

export function saveGame(token, game) {
    return callAction5(
        token,
        'AddGame',
        {
            game: beforeSaveGame(game),
        },
        true
    );
}

export function deleteGame(token, gamename) {
    return callAction5(token, 'DelGame', { nameOrToken: gamename });
}

/**
 * @typedef {Object} User
 * @property {string} name
 * @property {string} email
 * @property {string} game
 * @property {string} userToken
 *
 * @param {string} mastertoken
 * @param {string} gametoken
 * @returns {Promise<User[]>}
 */
export function getUsers(mastertoken, gametoken) {
    return new Promise((resolve, reject) => {
        callAction5(mastertoken, 'GetUsers', { nameOrToken: gametoken })
            .then((data) => {
                const userList = Object.entries(data).reduce(
                    (previous, [key, value]) => {
                        return [...previous, { ...value, userToken: key }];
                    },
                    []
                );
                resolve(userList);
            })
            .catch(reject);
    });
}

/*
{
        "username": "john",
        "useremail": "john@doe.com",
        "game": "b367fc90-bfe7-4e01-8143-b77c66405dfb",
        "status": "init",
        "simBeg": 43466.0,
        "simEnd": 43556.0,
        "stepsize": 7,
        "steps": 13,
        "step": 0,
        "maxAttempts": "4",
        "lastStepTime": 44363.33833510417,
        "lastSimTime": 44363.33833510417,
        "efficiency": 0.50183778190150508,
        "stepBeg": 43550.0,
        "stepEnd": 43556.0,
        "attempts": 4,
        "score": {
            "step": "finished",
            "efficiency": "0.50",
            "prod": "n.a.",
            "prof": "n.a."
        },
        "msg": "Ga naar de volgende periode",
        "usage": 0
    }
*/
export function getUserStatus(token, usertoken) {
    return callAction5(token, 'GetUserStatus', { usertoken });
}
export function resetUser(token, usertoken) {
    return callAction5(token, 'ResetUser', { usertoken });
}
export function deleteUser(token, usertoken) {
    return callAction5(token, 'DeleteUser', { usertoken });
}

export function registerTestUser(mastertoken, gametoken, name) {
    return callAction5(mastertoken, 'RegisterSelf', {
        username: name,
        nameOrToken: gametoken,
    });
}

export function resetAllUsers(mastertoken, gametoken) {
    return callAction5(mastertoken, 'ResetUsers', { nameOrToken: gametoken });
}

export function removeAccount(mastertoken) {
    return callAction5(mastertoken, 'RemoveAccount');
}

/**
 * @typedef {object} TGetResults
 * {
 *      <userToken>: {
 *          indicators: {},
 *          parameters: {preset:{}, game:{}},
 *          player: String (username),
 *          results: {},
 *          simdata:{}
 *      }
 * }
 *
 * @param {*} mastertoken
 * @param {*} gametoken
 * @returns {Promise<TGetResults>}
 */
export function getResults(mastertoken, gametoken) {
    return callAction5(mastertoken, 'GetResults', { nameOrToken: gametoken });
}
